import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

import { AiEnvironment } from '@azure/models';
import { environment } from 'environments/environment';

/** Dónde se recuerda el ambiente elegido entre visitas. */
const STORAGE_KEY = 'beliv.ai.environment';

/**
 * Qué par de colecciones está editando el módulo de IA. Los ambientes los
 * declara la API; si no conoce el endpoint la lista queda vacía, no se dibuja
 * el selector y no se manda `env`.
 */
@Injectable({ providedIn: 'root' })
export class AiEnvironmentService {
  private readonly url = `${environment.azureApiUrl}/api/ai/environments`;

  private readonly _environments = new BehaviorSubject<AiEnvironment[]>([]);
  private readonly _current = new BehaviorSubject<string | null>(null);

  private request$: Observable<AiEnvironment[]> | null = null;

  constructor(private _http: HttpClient) {
    // Se restaura antes de la primera petición; si al llegar la lista no
    // existe, se corrige ahí.
    this._current.next(this.readStored());
  }

  /** Los ambientes declarados. Vacío mientras no se hayan pedido. */
  get environments(): AiEnvironment[] {
    return this._environments.value;
  }

  get environments$(): Observable<AiEnvironment[]> {
    return this._environments.asObservable();
  }

  /** El elegido, que el interceptor pone en cada petición; `null` no manda nada. */
  get current(): string | null {
    return this._current.value;
  }

  /** Emite en cada cambio, para que las pantallas vuelvan a cargar. */
  get current$(): Observable<string | null> {
    return this._current.asObservable();
  }

  /** El ambiente elegido, completo. */
  get currentEnvironment(): AiEnvironment | null {
    return this.environments.find((item) => item.key === this.current) ?? null;
  }

  /** Hay algo que elegir: con uno solo no se dibuja el selector. */
  get hasChoice(): boolean {
    return this.environments.length > 1;
  }

  /** No es el ambiente por omisión: la pantalla lo marca. */
  get isSecondary(): boolean {
    const current = this.currentEnvironment;
    return !!current && current.default !== true;
  }

  /** Pide los ambientes una vez por sesión; `shareReplay` comparte la respuesta. */
  load(): Observable<AiEnvironment[]> {
    if (this.request$) return this.request$;

    this.request$ = this._http.get<AiEnvironment[]>(this.url).pipe(
      map((resp) => resp || []),
      catchError(() => of([] as AiEnvironment[])),
      tap((list) => this.apply(list)),
      shareReplay(1)
    );

    return this.request$;
  }

  /** Cambia de ambiente y devuelve si hubo cambio, para decidir si se recarga. */
  select(key: string): boolean {
    if (!key || key === this.current) return false;
    if (!this.environments.some((item) => item.key === key)) return false;

    this.store(key);
    this._current.next(key);
    return true;
  }

  // ─────────────────────────────────────────────────────────────────────────

  /** Elige el guardado si sigue existiendo, si no el de por omisión, si no nada. */
  private apply(list: AiEnvironment[]): void {
    this._environments.next(list);

    if (list.length === 0) {
      this.store(null);
      this._current.next(null);
      return;
    }

    const stored = this.current;
    if (stored && list.some((item) => item.key === stored)) {
      this._current.next(stored);
      return;
    }

    const fallback = list.find((item) => item.default === true) ?? list[0];
    this.store(fallback.key ?? null);
    this._current.next(fallback.key ?? null);
  }

  // El almacenamiento puede estar bloqueado (modo privado, políticas del
  // equipo); si falla solo se pierde recordar la elección.
  private readStored(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private store(key: string | null): void {
    try {
      if (key) localStorage.setItem(STORAGE_KEY, key);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      // sin persistencia, pero la sesión actual funciona igual
    }
  }
}
